import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const PIE_COLORS = {
  Pending: "#94A3B8",
  "In Progress": "#F59E0B",
  Delayed: "#EF4444",
  Completed: "#10B981",
};

// Hoisted style/config objects: stable references avoid re-creating props each render
const BAR_MARGIN = { top: 5, right: 5, left: -20, bottom: 0 };
const LINE_MARGIN = { top: 5, right: 10, left: -20, bottom: 0 };
const TICK_11 = { fontSize: 11, fill: "#64748B" };
const TICK_10 = { fontSize: 10, fill: "#64748B" };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid #E2E8F0" };
const LEGEND_STYLE = { fontSize: 11 };
const BAR_RADIUS = [4, 4, 0, 0];
const Y_DOMAIN_PCT = [0, 100];
const LINE_DOT = { r: 3 };
const otdTooltipFormatter = (value, name, props) => [`${value}% (${props.payload.completed} completed)`, "OTD"];

function Panel({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={testid}>
      <div className="mb-3">
        <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export default function AnalyticsCharts({ kpis }) {
  if (!kpis) return null;
  const pieData = (kpis.status_distribution || []).filter((d) => d.value > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Panel title="Capacity vs Load" subtitle="Daily shift capacity vs assigned volume" testid="chart-capacity-vs-load">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={kpis.capacity_vs_load} margin={BAR_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="name" tick={TICK_11} />
            <YAxis tick={TICK_11} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="capacity" name="Capacity" fill="#BFDBFE" radius={BAR_RADIUS} />
            <Bar dataKey="load" name="Load" fill="#2563EB" radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="Order Status Distribution" subtitle="Current pipeline breakdown" testid="chart-order-status-distribution">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={PIE_COLORS[entry.name]} />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={LEGEND_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      </Panel>

      <Panel title="On-Time Delivery Trend" subtitle="Live rolling 14-day OTD from completed orders" testid="chart-otd-trend">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={kpis.otd_trend} margin={LINE_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="day" tick={TICK_10} interval="preserveStartEnd" />
            <YAxis domain={Y_DOMAIN_PCT} tick={TICK_11} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={otdTooltipFormatter} />
            <Line type="monotone" dataKey="otd" stroke="#10B981" strokeWidth={2.5} dot={LINE_DOT} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}
